/**
 * Every number an admin page renders is formatted from here.
 *
 * The implementations stay where their unit lives — tokens with token usage,
 * bytes with BigQuery usage, durations with events — because those are used
 * outside the admin panel too. What this module adds is a single import site,
 * so "how does the panel show a count" has one answer and changing it is one
 * edit rather than four.
 */
import {
  formatBytesCompact,
  formatBytesExact,
  formatByteShare,
} from "@/features/bigquery-usage/lib/bytes";
import {
  formatCompactDurationMs,
  formatPreciseDurationMs,
} from "@/features/events/lib/event-timing";
import {
  formatTokenShare,
  formatTokensCompact,
  formatTokensExact,
} from "@/features/llm-usage/lib/format-tokens";
import { formatUsd } from "@/features/llm-usage/lib/money";
import { ADMIN_DASH } from "@/features/admin/components/layout/admin-layout-tokens";

export {
  formatBytesCompact,
  formatBytesExact,
  formatByteShare,
  formatCompactDurationMs,
  formatPreciseDurationMs,
  formatTokenShare,
  formatTokensCompact,
  formatTokensExact,
  formatUsd,
};

/** Grouped digits, in the reader's locale. Never abbreviated. */
export function formatCount(value: number, locale: string): string {
  return value.toLocaleString(locale);
}

/** A duration, or a dash when nothing was measured. Never a zero. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return ADMIN_DASH;
  return formatPreciseDurationMs(ms);
}

/**
 * One share, to one decimal place. Null when there is nothing to take a share
 * of, or when the row contributed nothing — "0.0%" beside a zero is noise.
 */
export function formatShare(value: number, total: number, locale: string): string | null {
  if (total <= 0 || value <= 0) return null;
  return `${((value / total) * 100).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}
